import { createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import z from 'zod';

import { useGetPrimaryAssetQuery } from '@/api/api-hooks';
import { PrimaryAsset } from '@/api/types/node';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { VersionControlIconWithDefault } from '@/components/Icons/VersionControlIcon';
import { AssetSettings } from '@/components/Settings/AssetSettings';
import { SheetTabs } from '@/components/SheetTabs/SheetTabs';
import { SheetWithHeader } from '@/components/SheetWithHeader';
import { SourceTeams } from '@/components/Sources/SourceTeams';
import { SourceUsers } from '@/components/Sources/SourceUsers';
import { assignmentTypeFilterValues } from '@/components/Sources/assignmentTypeItems';
import { sourceRoleFilterValues } from '@/components/Sources/sourceRoleItems';
import { useUser } from '@/hooks/useUser';
import { getOptionalAssetLink } from '@/utils/getAssetLink';
import { searchParam } from '@/utils/router';
import { optionalSortingSearchParam } from '@/utils/tableSorting';

const tabTypes = ['settings', 'teams', 'users'] as const;
type TabType = (typeof tabTypes)[number];

const sourceDetailsParamsSchema = z.object({
  tab: z.enum(tabTypes),
  sourceId: z.string(),
  organizationName: z.string(),
});

const sourceDetailsSearchSchema = z.object({
  pageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  pageIndex: fallback(z.number(), 0).default(0),
  add: fallback(z.boolean(), false).default(false),
  search: searchParam().optional(),
  role: z.enum(sourceRoleFilterValues).optional(),
  assignment: z.enum(assignmentTypeFilterValues).optional(),
  // Shared between tabs; cleared on tab switch since each tab has different sortable columns.
  sorting: optionalSortingSearchParam(),
});

export const Route = createFileRoute('/$organizationName/settings/_access/sources/$sourceId/$tab')({
  component: SourceSheetPage,
  params: { parse: sourceDetailsParamsSchema.parse },
  validateSearch: zodValidator(sourceDetailsSearchSchema),
});

function SourceSheetPage() {
  const { organizationDisplayName, routeOrganizationName } = useUser();
  const { sourceId, tab } = Route.useParams();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const { data: primaryAssetData, isLoading: isLoadingPrimaryAssetData } = useGetPrimaryAssetQuery(
    { id: sourceId, limit: 1, offset: 0 },
    { refetchOnWindowFocus: false }
  );

  const source = primaryAssetData?.results?.[0];

  useEffect(() => {
    let section = '';
    switch (tab) {
      case 'users':
        section = 'Users';
        break;
      case 'teams':
        section = 'Teams';
        break;
      case 'settings':
        section = 'Settings';
        break;
    }
    document.title = `${organizationDisplayName || 'Driver'} - ${source?.display_name || 'Source'} - ${section}`;
  }, [organizationDisplayName, source?.display_name, tab]);

  const assetName = source?.kind === PrimaryAsset.FILE ? 'PDF' : 'Codebase';

  const isAssetAdmin = source?.effective_role === 'asset_admin';
  const { to: titleTo, params: titleParams, search: titleSearch } = getOptionalAssetLink(source, routeOrganizationName);

  return (
    <SheetWithHeader
      isOpen
      renderHeaderSkeleton={isLoadingPrimaryAssetData}
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            to: '/$organizationName/settings/sources',
            params: { organizationName: routeOrganizationName },
            search: (prev) => ({
              ...prev,
              pageIndex: undefined,
              pageSize: undefined,
              add: undefined,
              assignment: undefined,
              search: undefined,
              sorting: undefined,
              role: undefined,
            }),
          });
        }
      }}
      title={source?.display_name || ''}
      description="Manage source settings and members"
      icon={<VersionControlIconWithDefault source={source?.provider} className="h-8 w-8" />}
      widthClassName="w-[90vw] !max-w-[1000px]"
      titleTo={titleTo}
      titleParams={titleParams}
      titleSearch={titleSearch}
    >
      <SheetTabs
        value={tab}
        onValueChange={(value) =>
          navigate({
            to: '/$organizationName/settings/sources/$sourceId/$tab',
            params: {
              organizationName: routeOrganizationName,
              sourceId: sourceId,
              tab: value as TabType,
            },
            // 🐉🐉🐉
            // We need to keep the search params otherwise the useEffect for the debounced search value will be triggered
            // unintentionally and redirect us back to the previous page.
            search: (prev) => ({
              ...prev,
              pageIndex: undefined,
              pageSize: undefined,
              add: undefined,
              search: undefined,
              sorting: undefined,
              role: undefined,
            }),
          })
        }
        tabs={[
          {
            value: 'users',
            label: 'Users',
            content: (
              <SourceUsers
                route={Route}
                sourceId={sourceId}
                sourceName={source?.display_name}
                backTo="/$organizationName/settings/sources"
                backParams={{ organizationName: routeOrganizationName }}
                backSearch={search}
              />
            ),
          },
          {
            value: 'teams',
            label: 'Teams',
            content: (
              <SourceTeams
                route={Route}
                sourceId={sourceId}
                sourceName={source?.display_name}
                backTo="/$organizationName/settings/sources"
                backParams={{ organizationName: routeOrganizationName }}
                backSearch={search}
              />
            ),
          },
          {
            value: 'settings',
            label: 'Settings',
            content: (
              <AssetSettings
                assetId={sourceId}
                assetName={assetName}
                isAssetAdmin={isAssetAdmin}
                deleteMessage={`Once you remove this ${assetName.toLowerCase()} from Driver, there is no going back. Please be certain.`}
                backTo="/$organizationName/settings/sources"
                backParams={{ organizationName: routeOrganizationName }}
                backSearch={search}
              />
            ),
          },
        ]}
      />
    </SheetWithHeader>
  );
}
